import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { listLeftovers, reconcileScope, type LeftoverBlock, type ReconcileDecision, type ReconcileOutcome, type ReconcileTarget } from '../utils/discoveryGridApi';
import { LEVEL_FILL } from './DiscoveryGrid';

/**
 * Reconcile scope (DISCOVERY_GRID_REDESIGN_SPEC §4.10): the previous
 * engagement's leftovers (unfinished cells, and cells parked for a CO or a
 * later engagement) with one outcome per block: carry into the target, leave
 * where they are, hand to the client's team, or drop with a reason. Writes the
 * marks and the scope map on the target document.
 */

export interface ScopeReconcileDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  accessToken: string;
  /** Where leftovers may go */
  target: ReconcileTarget;
  /** Engagements of the company to pick the source from (excluding the target) */
  engagements: Array<{ id: string; name?: string; title?: string; status?: string }>;
  defaultFromEngagementId?: string | null;
  onDone?: (result: { carried: number; toClient: number; dropped: number; left: number }) => void;
  /** Label of the dismiss button ("Cancel" by default; "Skip for now" when offered right after a new SOW) */
  cancelLabel?: string;
  /** Replaces the default description (e.g. when opened automatically after Add SOW) */
  intro?: string;
}

const OUTCOME_LABELS: Record<ReconcileOutcome, string> = {
  carry: 'Carry into this document',
  leave: 'Leave where it is',
  client: "Hand to the client's team",
  drop: 'Drop (reason required)',
};

/**
 * Default outcome per block: carry when its cells were parked for this kind of
 * document or when the server matched it to one of the target's scope items
 * (spec §4.10 step 1); otherwise leave. Exported for tests.
 */
export function defaultOutcomes(blocks: LeftoverBlock[], targetType: ReconcileTarget['type']): Record<string, ReconcileOutcome> {
  const next: Record<string, ReconcileOutcome> = {};
  for (const b of blocks) {
    const parkedHere = b.cells.some((c) => c.descoped && c.descoped.to === targetType);
    next[b.blockId] = parkedHere ? 'carry' : b.proposed?.outcome ?? 'leave';
  }
  return next;
}

export function ScopeReconcileDialog({ open, onClose, companyId, accessToken, target, engagements, defaultFromEngagementId, onDone, cancelLabel = 'Cancel', intro }: ScopeReconcileDialogProps) {
  const sources = useMemo(() => engagements.filter((e) => e.id !== target.engagementId), [engagements, target.engagementId]);
  const [from, setFrom] = useState<string>(defaultFromEngagementId ?? sources[0]?.id ?? '');
  const [blocks, setBlocks] = useState<LeftoverBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, ReconcileOutcome>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [scopeItems, setScopeItems] = useState(0);

  useEffect(() => {
    if (!open) return;
    setFrom(defaultFromEngagementId ?? sources[0]?.id ?? '');
  }, [open, defaultFromEngagementId, sources]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    listLeftovers(companyId, from || null, accessToken, target)
      .then((r) => {
        if (cancelled) return;
        setBlocks(r.blocks);
        setScopeItems(r.scopeItems ?? 0);
        setOutcomes(defaultOutcomes(r.blocks, target.type));
        setReasons({});
      })
      .catch((e) => toast.error(e?.message || 'Could not load the leftovers'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, from, accessToken, target.type, target.engagementId, target.ref]);

  const decisions: ReconcileDecision[] = useMemo(() => blocks.map((b) => {
    const outcome = outcomes[b.blockId] ?? 'leave';
    const matched = outcome === 'carry' && b.proposed ? { scopeItemId: b.proposed.scopeItemId, scopeItemLabel: b.proposed.scopeItemLabel } : {};
    return { blockId: b.blockId, outcome, reason: reasons[b.blockId]?.trim() || undefined, text: b.name, ...matched };
  }), [blocks, outcomes, reasons]);
  const proposedCount = useMemo(() => blocks.filter((b) => b.proposed).length, [blocks]);
  const counts = useMemo(() => decisions.reduce((acc, d) => { acc[d.outcome] = (acc[d.outcome] ?? 0) + 1; return acc; }, {} as Record<string, number>), [decisions]);
  const invalid = decisions.some((d) => d.outcome === 'drop' && !d.reason);
  const anyAction = decisions.some((d) => d.outcome !== 'leave');

  const submit = async () => {
    try {
      setBusy(true);
      const result = await reconcileScope(companyId, target, from || null, decisions.filter((d) => d.outcome !== 'leave'), accessToken);
      toast.success(`Scope reconciled: ${result.carried} carried, ${result.toClient} to the client, ${result.dropped} dropped`);
      onDone?.(result);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Reconcile failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reconcile scope with a previous engagement</DialogTitle>
          <DialogDescription>
            {intro ?? `${target.label ?? (target.type === 'change_order' ? target.ref : 'this engagement')} decides what comes along. Leftovers below are the source engagement's unfinished cells and the cells parked for a change order or a later engagement. Carried cells keep their answers and position; "leave" changes nothing.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-600">Leftovers from</span>
          <Select value={from} onValueChange={setFrom} disabled={sources.length === 0}>
            <SelectTrigger className="w-[320px] h-9 text-sm"><SelectValue placeholder="Choose the previous engagement" /></SelectTrigger>
            <SelectContent>
              {sources.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.title || e.id}{e.status ? ` · ${e.status}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          {sources.length === 0 && <span className="text-xs text-gray-500">This company has no other engagement to carry from.</span>}
        </div>

        {!loading && blocks.length > 0 && (
          <p className="text-xs text-gray-500" data-testid="reconcile-proposals-note">
            {scopeItems === 0
              ? 'The new document has no extracted scope items yet, so nothing is pre-matched: pick per block.'
              : proposedCount === 0
                ? `None of the ${scopeItems} scope items in the new document matched a leftover block by name: pick per block.`
                : `${proposedCount} of ${blocks.length} blocks matched a scope item in the new document and are pre-selected to carry. Change any row.`}
          </p>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><Loader2 className="size-4 animate-spin" /> Loading leftovers…</div>
        ) : blocks.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">Nothing left over: every cell in that engagement is locked or already resolved.</div>
        ) : (
          <ul className="divide-y divide-gray-200 border rounded-md">
            {blocks.map((b) => {
              const outcome = outcomes[b.blockId] ?? 'leave';
              return (
                <li key={b.blockId} className="p-3 flex flex-col gap-2" data-testid={`reconcile-block-${b.blockId}`} data-outcome={outcome}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{b.name}{b.guided ? <span className="ml-2 text-[10px] font-semibold px-1 rounded bg-gray-100 text-gray-600">Guided</span> : null}</div>
                      {b.proposed && (
                        <div className="text-xs text-teal-800 mt-0.5 inline-block px-1 rounded bg-teal-50 border border-teal-200" title={`Matched by block name and question text (score ${b.proposed.score}). Change the outcome if this is wrong.`} data-testid={`reconcile-match-${b.blockId}`}>
                          Matched: {b.proposed.scopeItemLabel}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">{formatCategoryLabel(b.category)} · {b.cells.length} of {b.total} cells left over</div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {b.cells.slice(0, 40).map((c) => (
                          <span
                            key={c.cellId}
                            title={`${c.text} · ${c.descoped ? `parked: ${c.descoped.label ?? c.descoped.to}` : c.levelLabel}${c.answerPreview ? ` · ${c.answerPreview}` : ''}`}
                            className={`inline-block w-3 h-3 ${c.kind === 'artifact' ? 'rounded-full' : 'rounded-[2px]'}`}
                            style={c.descoped ? { backgroundColor: '#fff', border: '1.5px solid #2563EB', boxSizing: 'border-box' } : { backgroundColor: LEVEL_FILL[c.level as 0 | 1 | 2 | 3 | 4 | 5] }}
                          />
                        ))}
                        {b.cells.length > 40 && <span className="text-[10px] text-gray-500">+{b.cells.length - 40}</span>}
                      </div>
                    </div>
                    <Select value={outcome} onValueChange={(v) => setOutcomes((prev) => ({ ...prev, [b.blockId]: v as ReconcileOutcome }))}>
                      <SelectTrigger className="w-[230px] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(Object.keys(OUTCOME_LABELS) as ReconcileOutcome[]).map((o) => <SelectItem key={o} value={o}>{OUTCOME_LABELS[o]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {outcome === 'drop' && (
                    <Input value={reasons[b.blockId] ?? ''} onChange={(e) => setReasons((prev) => ({ ...prev, [b.blockId]: e.target.value }))} placeholder="Why is this out of scope? (kept on the record)" className="h-8 text-xs" />
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter className="flex items-center justify-between gap-3 sm:justify-between">
          <div className="text-xs text-gray-500">{counts.carry ?? 0} carry · {counts.leave ?? 0} leave · {counts.client ?? 0} to client · {counts.drop ?? 0} drop</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>{cancelLabel}</Button>
            <Button onClick={submit} disabled={busy || loading || invalid || !anyAction} className="bg-[#0B4F6C] hover:bg-[#0a4660] text-white">
              {busy ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}Apply scope map
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
